import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Anchor, SimpleGrid, Stack, Text, ThemeIcon } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import type { HubOutletContext } from "../../EnterpriseHub";
import type { EntitlementMap } from "../../../types/entitlements";
import { useStartTrial } from "../../../hooks/useStartTrial";
import { InlineNotice } from "../../../components/InlineNotice";
import { ProductBlock } from "../../../components/ProductBlock";
import { ProvisioningNotice } from "../../../components/ProvisioningNotice";
import { cardSetupState } from "../../../lib/provisioning";
import { productCatalogue } from "../../../catalogue/products";
import { CONTACT_URL } from '../../../lib/externalLinks';

export function NoEntitlementScenario() {
  const { state, isOrgAdmin, refetch, setup, trialIntentBusy, trialStarted } = useOutletContext<HubOutletContext>();
  const trial = useStartTrial({ onStarted: (productKey) => { setup.watch(productKey); refetch(); trialStarted(productKey); } });
  const setupProduct = productCatalogue.find((p) => p.key === setup.productKey);
  // Which products are trialable comes from /me — the server's verdict, not a hub list. The
  // last ready map is kept while a refetch is in flight, so starting a trial doesn't flip the
  // just-clicked card to "Soon" mid-celebration. Adjusted during render (not in an effect nor a
  // ref) per https://react.dev/learn/you-might-not-need-an-effect, mirroring OfferDrawer.
  const [lastReadyProducts, setLastReadyProducts] = useState<EntitlementMap>({});
  if (state?.kind === 'ready' && state.data.products !== lastReadyProducts) {
    setLastReadyProducts(state.data.products);
  }
  const products = state?.kind === 'ready' ? state.data.products : lastReadyProducts;

  return (
    <Stack gap={24}>
      {/* Once a build is being watched, the banner carries the story — keeping the
          no-applications panel up would contradict it for the beat before the refetch
          swaps this scenario out. */}
      {setup.state === 'idle' && (
        <Stack gap={5} p={24} bdrs={'lg'} bg={'var(--bg-surface)'} bd={'1px solid var(--border-default)'}>
          <ThemeIcon size={42} bg={'var(--bg-subtle)'} c={'var(--text-subtle)'}>
            <IconAlertCircle size={20} />
          </ThemeIcon>
          <Text fz={16} fw={700}>No applications available yet</Text>
          <Text fz={'sm'} c={'var(--text-muted)'}>
            Your organisation doesn't have any active applications. Start a free trial of one below{isOrgAdmin ? '.' : ', or ask your organisation admin for access.'}
          </Text>
        </Stack>
      )}

      <Stack gap="sm">
        {setup.state === 'idle' && (
          <Text fz={'xs'} fw={700} tt={'uppercase'} c={'var(--text-subtle)'}>Start a trial</Text>
        )}

        {trial.error && <InlineNotice tone="error" message={trial.error} onClose={trial.clear} />}
        {setupProduct && setup.state !== 'idle' ? (
          <ProvisioningNotice product={setupProduct} state={setup.state} stages={setup.stages} onClose={setup.dismiss} />
        ) : (
          trial.success && <InlineNotice tone="success" message={trial.success} onClose={trial.clear} />
        )}

        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
          {/* isLocked keeps the cards from rendering as live links into apps the org has no access to. */}
          {productCatalogue.filter((product) => !product.notYetOffered).map((product) => {
            if (products[product.key]?.trialAvailable) {
              return isOrgAdmin ? (
                <ProductBlock
                  key={product.key}
                  product={product}
                  entitlement={undefined}
                  isLocked
                  ctaLabel={`Try ${product.name}`}
                  onCta={() => trial.start(product.key)}
                  ctaBusy={trial.busyProducts.has(product.key)}
                  ctaDisabled={trialIntentBusy}
                  setupState={cardSetupState(product.key, setup)}
                />
              ) : (
                <ProductBlock
                  key={product.key}
                  product={product}
                  entitlement={undefined}
                  isLocked
                  ctaLabel="Ask your organisation admin"
                />
              );
            }

            return (
              <ProductBlock
                key={product.key}
                product={product}
                entitlement={undefined}
                isLocked
                ctaLabel={`Try ${product.name}`}
                badge={{ title: 'Soon', color: 'var(--text-muted)' }}
              />
            );
          })}
        </SimpleGrid>
      </Stack>

      <Text fz={'xs'} c={'var(--text-subtle)'}>
        Need help?{' '}
        <Anchor href={CONTACT_URL} target="_blank" rel="noreferrer" fz={'xs'} c={'var(--text-muted)'}>
          Contact support
        </Anchor>
      </Text>
    </Stack>
  );
}
